// Phase 10B — Calendar grid restyled in the AutoMind 2050 design language.
// Same logic and rendering approach; ink + gold palette, gold "today" dot,
// gold left-edge for the day cell when it has appointments.

import React from 'react';
import { DataCard } from '../../components/ui/data-card';

const STATUS_TONE_BG = {
  scheduled: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20',
  confirmed: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  completed: 'bg-ink-200/60 text-ink-700 dark:bg-ink-800/60 dark:text-ink-200 border-ink-300/40',
  cancelled: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20',
  no_show: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
};

const getStatusClass = (status) =>
  STATUS_TONE_BG[status] ||
  'bg-ink-100 text-ink-700 dark:bg-ink-900/60 dark:text-ink-200 border-ink-300/40';

const getDirectionDot = (callDirection) =>
  callDirection === 'inbound' ? 'bg-emerald-500' : 'bg-sky-500';

const formatTime = (timeString) => {
  if (!timeString) return 'TBD';
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = String(minutes).padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

const CalendarGrid = ({ selectedDate, appointments, onChangeMonth, onSetToday, view }) => {
  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getAppointmentsForDate = (day) => {
    if (!day) return [];
    const dateToCheck = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      day
    );
    return appointments.filter((apt) => {
      const aptDate = new Date(apt.appointment_date);
      return aptDate.toDateString() === dateToCheck.toDateString();
    });
  };

  const headerActions = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="btn-2050 btn-ghost"
        onClick={() => onChangeMonth(-1)}
      >
        ‹
      </button>
      <button
        type="button"
        className="btn-2050 btn-secondary"
        onClick={onSetToday}
      >
        Today
      </button>
      <button
        type="button"
        className="btn-2050 btn-ghost"
        onClick={() => onChangeMonth(1)}
      >
        ›
      </button>
    </div>
  );

  const monthLabel = selectedDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  // Day / Week views are placeholders that fall back to a tasteful EmptyState
  // — the previous implementation only rendered the month grid. Keeping the
  // exact same data path preserves all callers.
  if (view && view !== 'month') {
    return (
      <DataCard
        title={monthLabel}
        description={`${view === 'day' ? 'Day' : 'Week'} view`}
        actions={headerActions}
      >
        <div className="py-8 text-center text-sm text-ink-500">
          {view === 'day' ? 'Day view' : 'Week view'} is not available yet — switch
          back to Month for the full calendar grid.
        </div>
      </DataCard>
    );
  }

  return (
    <DataCard
      title={monthLabel}
      description="Tap a day to see appointments"
      actions={headerActions}
    >
      <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.16em] text-ink-500 mb-3">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>Inbound</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
          <span>Outbound</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          <span>Today</span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-[10px] uppercase tracking-[0.16em] font-medium text-ink-500 py-2"
          >
            {day}
          </div>
        ))}

        {getDaysInMonth().map((day, index) => {
          const dayAppointments = getAppointmentsForDate(day);
          const isToday =
            day &&
            new Date().toDateString() ===
              new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                day
              ).toDateString();

          return (
            <div
              key={index}
              className={`min-h-[88px] p-2 rounded-lg border transition-colors ${
                day
                  ? isToday
                    ? 'bg-gold/5 border-gold/40'
                    : dayAppointments.length > 0
                    ? 'bg-white dark:bg-ink-900/30 border-ink-200/70 dark:border-ink-800/70 hover:border-gold/50'
                    : 'bg-ink-50/40 dark:bg-ink-900/20 border-transparent hover:bg-ink-50 dark:hover:bg-ink-900/40'
                  : 'bg-transparent border-transparent'
              }`}
            >
              {day && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium nums ${
                        isToday
                          ? 'text-ink-900 dark:text-ink-50'
                          : 'text-ink-700 dark:text-ink-300'
                      }`}
                    >
                      {day}
                    </span>
                    {isToday && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-gold"
                        title="Today"
                      />
                    )}
                  </div>
                  {dayAppointments.length > 0 && (
                    <div className="space-y-1">
                      {dayAppointments.slice(0, 2).map((apt) => (
                        <div
                          key={apt.id}
                          className={`text-[10px] px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${getStatusClass(
                            apt.status
                          )}`}
                          title={`${apt.customer_name} — ${apt.status}`}
                        >
                          <span
                            className={`w-1 h-1 rounded-full ${getDirectionDot(
                              apt.call_direction
                            )} flex-shrink-0`}
                          />
                          <span className="truncate nums">
                            {formatTime(apt.appointment_time)}
                          </span>
                        </div>
                      ))}
                      {dayAppointments.length > 2 && (
                        <div className="text-[10px] text-ink-500">
                          +{dayAppointments.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </DataCard>
  );
};

export default CalendarGrid;
